<script lang="ts">
  import { onMount } from 'svelte';
  import { t } from '../i18n.ts';
  import { listSaves, SLOTS, type SaveGame, type SlotId } from '../game/save.ts';
  import { game } from '../game/game.ts';
  import Settings from './Settings.svelte';
  import { feature } from '../../shared/features.ts';
  import AccountPanel from './AccountPanel.svelte';
  import { account } from '../api/account.ts';
  import { installPrompt, online } from '../pwa.ts';
  import { toggleFullscreen } from '../fullscreen.ts';

  let {
    ready,
    hasSave,
    started,
    onPlay,
    onNew,
    onLoad,
    onShow,
    onDaily,
    onPub,
  }: { ready: boolean; hasSave: boolean; started: boolean; onPlay: () => void; onNew: () => void; onLoad: (s: SlotId) => void; onShow: () => void; onDaily: () => void; onPub: () => void } = $props();
  let view = $state<'main' | 'guides' | 'about' | 'saves' | 'settings' | 'chapters' | 'account'>('main');
  let saves = $state<Record<string, SaveGame>>({});

  async function openSaves() {
    view = 'saves';
    saves = Object.fromEntries((await listSaves()).map((s) => [s.slot, s]));
  }
  async function saveSlot(slot: SlotId) {
    await game.saveTo(slot);
    await openSaves();
  }
  const fmtDate = (n: number) => new Date(n).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  let guides = $state<{ stage: string; title: string; file: string; date: string }[]>([]);
  let chapters = $state<{ id: string; title: string }[]>([]);
  const currentChapter = new URLSearchParams(location.search).get('chapter') ?? 'ch1';

  onMount(async () => {
    if (feature.editor) fetch('/api/chapters').then(async (r) => (chapters = r.ok ? await r.json() : []));
    try {
      const r = await fetch('/guides/index.json');
      if (r.ok) guides = (await r.json()).reverse();
    } catch {
      /* offline: keine Liste */
    }
  });
</script>

<div class="menu" role="dialog" aria-modal="true" aria-labelledby="title">
  <div class="card panel">
    <div class="sign">
      <span class="kicker">{t('menu.kicker')}</span>
      <h1 id="title">{t('menu.title')}</h1>
      <span class="sub">{t('menu.subtitle')}</span>
    </div>

    {#if view === 'main'}
      {#if !$online}<p class="offline" role="status" data-testid="offline-note">📴 {t('menu.offline')}</p>{/if}
      <nav class="buttons">
        <button class="btn primary" data-testid="play" disabled={!ready} onclick={onPlay}>
          {!ready ? t('menu.loading') : started || hasSave ? t('menu.continue') : t('menu.play')}
        </button>
        {#if started || hasSave}
          <button class="btn" data-testid="new-game" disabled={!ready} onclick={onNew}>{t('menu.newGame')}</button>
        {/if}
        <button class="btn quiz" data-testid="quiz-night" disabled={!ready} onclick={onShow}>{t('menu.quizNight')}</button>
        {#if feature.contentEngine}<button class="btn" data-testid="daily-btn" onclick={onDaily}>{t('menu.daily')}</button>{/if}
        {#if feature.meta}<button class="btn" data-testid="pub-btn" onclick={onPub}>🪙 {t('meta.open')}</button>{/if}
        {#if feature.online}<button class="btn quiz" data-testid="online-btn" onclick={() => (location.href = '/online')}>🌐 {t('menu.online')}</button>{/if}
        {#if feature.party}<button class="btn quiz" data-testid="party-mode" onclick={() => (location.href = '/host')}>{t('menu.partyMode')}</button>{/if}
        {#if chapters.length > 1}
          <button class="btn" data-testid="chapters-btn" onclick={() => (view = 'chapters')}>{t('menu.chapters')}</button>
        {/if}
        <button class="btn" data-testid="saves" disabled={!ready} onclick={openSaves}>{t('menu.saves')}</button>
        {#if feature.online}<button class="btn" data-testid="account-btn" onclick={() => (view = 'account')}>{$account ? '👤 ' + $account.nickname : t('menu.account')}</button>{/if}
        {#if $installPrompt}<button class="btn" data-testid="install-btn" onclick={() => $installPrompt?.prompt()}>📲 {t('menu.install')}</button>{/if}
        {#if document.documentElement.requestFullscreen}<button class="btn fs" data-testid="fullscreen-btn" onclick={toggleFullscreen}>⛶ {t('menu.fullscreen')}</button>{/if}
        <button class="btn" data-testid="settings-btn" onclick={() => (view = 'settings')}>{t('menu.settings')}</button>
        <button class="btn" data-testid="guides" onclick={() => (view = 'guides')}>{t('menu.guide')}</button>
        <button class="btn" onclick={() => (view = 'about')}>{t('menu.about')}</button>
      </nav>
    {:else if view === 'guides'}
      <div class="list" data-testid="guide-list">
        <h2>{t('menu.guideTitle')}</h2>
        {#if guides.length === 0}
          <p>{t('menu.noGuides')}</p>
        {/if}
        {#each guides as g, i}
          <a class="guide" href={'/guides/' + g.file} target="_blank" rel="noopener" data-testid={'guide-' + g.stage}>
            <span class="stage">{g.stage.toUpperCase()}</span>
            <span class="gt">{g.title}{#if i === 0}<em> · {t('menu.newest')}</em>{/if}</span>
            <span class="pdf">PDF ↓</span>
          </a>
        {/each}
        <button class="btn" onclick={() => (view = 'main')}>{t('menu.back')}</button>
      </div>
    {:else if view === 'chapters'}
      <div class="list" data-testid="chapter-list">
        <h2>{t('menu.chapters')}</h2>
        {#each chapters as c}
          <a class="guide" href={'/?chapter=' + c.id} data-testid={'chapter-' + c.id}>
            <span class="stage">{c.id.toUpperCase()}</span>
            <span class="gt">{c.title}{#if c.id === currentChapter}<em> · {t('menu.current')}</em>{/if}</span>
            <span class="pdf">▶</span>
          </a>
        {/each}
        <button class="btn" onclick={() => (view = 'main')}>{t('menu.back')}</button>
      </div>
    {:else if view === 'account'}
      <div class="list">
        <h2>{t('account.title')}</h2>
        <AccountPanel onBack={() => (view = 'main')} />
      </div>
    {:else if view === 'settings'}
      <Settings onBack={() => (view = 'main')} />
    {:else if view === 'saves'}
      <div class="list" data-testid="save-list">
        <h2>{t('menu.saves')}</h2>
        {#each SLOTS as slot}
          {@const sv = saves[slot]}
          <div class="slot">
            <div class="sinfo">
              <b>{t('menu.slot.' + slot)}</b>
              <small>{sv ? `${sv.sceneName} · ${fmtDate(sv.savedAt)}` : t('menu.emptySlot')}</small>
            </div>
            {#if started && slot !== 'auto'}
              <button class="btn small" onclick={() => saveSlot(slot)} data-testid={'save-' + slot}>{t('menu.save')}</button>
            {/if}
            <button class="btn small" disabled={!sv} onclick={() => onLoad(slot)} data-testid={'load-' + slot}>{t('menu.load')}</button>
          </div>
        {/each}
        <button class="btn" onclick={() => (view = 'main')}>{t('menu.back')}</button>
      </div>
    {:else}
      <div class="list">
        <h2>{t('menu.about')}</h2>
        <p>{t('menu.aboutText')}</p>
        <button class="btn" onclick={() => (view = 'main')}>{t('menu.back')}</button>
      </div>
    {/if}
  </div>
</div>

<style>
  .menu {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    padding: calc(16px + var(--safe-top)) 16px calc(16px + var(--safe-bottom));
    z-index: 40;
    background: radial-gradient(ellipse at 50% 40%, rgba(224, 163, 70, 0.12), rgba(12, 20, 34, 0.5) 70%);
    /* viele Menüpunkte: auf kleinen Bildschirmen scrollen statt abschneiden */
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .card {
    width: min(460px, 100%);
    padding: 28px 24px 24px;
    text-align: center;
    animation: rise 0.7s ease both;
  }
  @keyframes rise {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
  }
  .sign {
    margin-bottom: 22px;
  }
  .kicker {
    font-family: var(--font-hand);
    font-size: 1.5rem;
    color: var(--amber2);
  }
  h1 {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: clamp(2.4rem, 9vw, 3.4rem);
    line-height: 1;
    margin: 4px 0 8px;
    color: var(--amber3);
    text-shadow: 0 0 24px rgba(246, 200, 109, 0.35), 0 3px 0 rgba(63, 37, 22, 0.9);
  }
  .sub {
    color: var(--paper);
    opacity: 0.8;
  }
  .buttons,
  .list {
    display: grid;
    gap: 12px;
  }
  /* Nebenpunkte zweispaltig, damit das Menü kompakt bleibt (Hauptknöpfe über die volle Breite) */
  .buttons {
    grid-template-columns: 1fr 1fr;
  }
  .buttons :global(.btn.primary),
  .buttons :global(.btn.quiz) {
    grid-column: 1 / -1;
  }
  .buttons :global(.btn) {
    padding-left: 8px;
    padding-right: 8px;
  }
  @media (pointer: coarse) {
    .fs {
      display: none;
    }
  }
  h2 {
    font-family: var(--font-display);
    margin: 0 0 4px;
    color: var(--amber2);
  }
  .list p {
    text-align: left;
    line-height: 1.5;
  }
  .guide {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 12px;
    align-items: center;
    min-height: var(--touch);
    padding: 10px 14px;
    border-radius: 10px;
    background: rgba(12, 20, 34, 0.5);
    border: 1px solid var(--panel-edge);
    color: var(--white);
    text-decoration: none;
    text-align: left;
  }
  .guide:hover {
    border-color: var(--amber2);
  }
  .guide .stage {
    font-family: var(--font-display);
    color: var(--amber2);
    font-weight: 800;
  }
  .guide em {
    color: var(--amber2);
    font-style: normal;
    font-size: 0.85em;
  }
  .slot {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(12, 20, 34, 0.5);
    border: 1px solid var(--panel-edge);
    text-align: left;
  }
  .sinfo {
    flex: 1;
    display: grid;
  }
  .sinfo small {
    opacity: 0.75;
  }
  .btn.small {
    padding: 0.4em 0.9em;
    min-height: 44px;
    font-size: 0.9rem;
  }
  .pdf {
    font-size: 0.85em;
    opacity: 0.8;
  }
  .offline {
    text-align: center;
    color: var(--amber3);
    margin: 0 0 8px;
  }
</style>
